import React from 'react';
import { useNavigate } from 'react-router-dom';
import SensorCard from '../components/SensorCard';
import { 
  TrendingUp, 
  TrendingDown, 
  Activity,
  AlertCircle,
  Thermometer,
  Droplets
} from 'lucide-react';

const Dashboard = ({ sensors }) => {
  const navigate = useNavigate();

  const handleSensorClick = (sensor) => {
    navigate(`/sensor/${sensor.id}`);
  };

  // Statistiques générales
  const totalSensors = sensors.length;
  const activeSensors = sensors.filter(s => s.status === 'active').length;
  const warningSensors = sensors.filter(s => s.status === 'warning').length;
  const offlineSensors = sensors.filter(s => s.status === 'offline').length;

  // Moyennes
  const avgHumidity = sensors.length > 0 
    ? (sensors.reduce((acc, sensor) => acc + sensor.humidity, 0) / sensors.length).toFixed(1)
    : 0;
  
  const avgTemperature = sensors.length > 0 
    ? (sensors.reduce((acc, sensor) => acc + sensor.temperature, 0) / sensors.length).toFixed(1)
    : 0;

  const stats = [
    {
      name: 'Capteurs actifs',
      value: activeSensors,
      total: totalSensors,
      icon: Activity,
      color: 'success',
      bgColor: 'from-success-50 to-success-100',
      change: '+2.5%',
      changeType: 'increase'
    },
    {
      name: 'Alertes',
      value: warningSensors + offlineSensors,
      total: totalSensors,
      icon: AlertCircle,
      color: warningSensors > 0 || offlineSensors > 0 ? 'warning' : 'success',
      bgColor: warningSensors > 0 || offlineSensors > 0 ? 'from-warning-50 to-warning-100' : 'from-success-50 to-success-100',
      change: warningSensors > 0 || offlineSensors > 0 ? '+1' : '0',
      changeType: warningSensors > 0 || offlineSensors > 0 ? 'increase' : 'stable'
    },
    {
      name: 'Humidité moyenne',
      value: `${avgHumidity}%`,
      icon: Droplets,
      color: 'primary',
      bgColor: 'from-primary-50 to-primary-100',
      change: '-0.3%',
      changeType: 'decrease'
    },
    {
      name: 'Température moyenne',
      value: `${avgTemperature}°C`,
      icon: Thermometer,
      color: 'indigo',
      bgColor: 'from-indigo-50 to-indigo-100',
      change: '+0.8°C',
      changeType: 'increase'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-fade-in">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gradient mb-2">
          Tableau de bord
        </h1>
        <p className="text-slate-600">
          Surveillance en temps réel de vos capteurs d'environnement
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div 
              key={stat.name} 
              className={`glass-effect rounded-2xl p-6 card-hover animate-slide-up`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-center justify-between">
                <div className={`p-3 bg-gradient-to-r ${stat.bgColor} rounded-xl`}>
                  <Icon className={`h-6 w-6 text-${stat.color}-600`} />
                </div>
                <div className="flex items-center space-x-1">
                  {stat.changeType === 'increase' ? (
                    <TrendingUp className="h-4 w-4 text-success-500" />
                  ) : stat.changeType === 'decrease' ? (
                    <TrendingDown className="h-4 w-4 text-danger-500" />
                  ) : (
                    <div className="h-4 w-4" />
                  )}
                  <span className={`text-sm font-medium ${
                    stat.changeType === 'increase' ? 'text-success-600' :
                    stat.changeType === 'decrease' ? 'text-danger-600' :
                    'text-slate-500'
                  }`}>
                    {stat.change}
                  </span>
                </div>
              </div>
              
              <div className="mt-4">
                <div className="flex items-baseline space-x-2">
                  <p className="text-3xl font-bold text-slate-800">
                    {stat.value}
                  </p>
                  {stat.total && (
                    <p className="text-lg text-slate-500">
                      /{stat.total}
                    </p>
                  )}
                </div>
                <p className="text-sm font-medium text-slate-600 mt-1">
                  {stat.name}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="glass-effect rounded-2xl p-6">
        <h2 className="text-xl font-semibold text-slate-800 mb-4">
          Actions rapides
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button 
            onClick={() => navigate('/analytics')}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 transform hover:scale-105"
          >
            <TrendingUp className="h-5 w-5" />
            <span>Voir les analytiques</span>
          </button>
          
          <button 
            onClick={() => navigate('/settings')}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 rounded-xl hover:from-slate-200 hover:to-slate-300 transition-all duration-300 transform hover:scale-105"
          >
            <AlertCircle className="h-5 w-5" />
            <span>Configurer alertes</span>
          </button>
          
          <button className="flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-xl hover:from-indigo-600 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105">
            <Activity className="h-5 w-5" />
            <span>Rapport détaillé</span>
          </button>
        </div>
      </div>

      {/* Sensors Grid */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-800">
            Capteurs ({totalSensors})
          </h2>
          
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div className="h-3 w-3 bg-success-500 rounded-full"></div>
              <span className="text-sm text-slate-600">{activeSensors} Actifs</span>
            </div>
            {warningSensors > 0 && (
              <div className="flex items-center space-x-1">
                <div className="h-3 w-3 bg-warning-500 rounded-full"></div>
                <span className="text-sm text-slate-600">{warningSensors} Alertes</span>
              </div>
            )}
            {offlineSensors > 0 && (
              <div className="flex items-center space-x-1">
                <div className="h-3 w-3 bg-danger-500 rounded-full"></div>
                <span className="text-sm text-slate-600">{offlineSensors} Hors ligne</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {sensors.map((sensor, index) => (
            <div
              key={sensor.id}
              className="animate-slide-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <SensorCard 
                sensor={sensor} 
                onClick={handleSensorClick}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {sensors.length === 0 && (
        <div className="text-center py-12">
          <Activity className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-500 mb-2">
            Aucun capteur détecté
          </h3>
          <p className="text-slate-400">
            Connectez vos premiers capteurs pour commencer le monitoring
          </p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;